import { Metadata } from "next";
import { ISbStoriesParams } from "@storyblok/react/rsc";
import pLimit from "p-limit";
import { getNextCachingParams } from "./utils";
import { ISbLink } from "storyblok-js-client";
import StoryblokClient from "storyblok-js-client";
import type { Page } from "@/generated/sb";
import { ISbStoryData } from "storyblok-js-client";
import { RESOLVE_RELATIONS } from "@/constants/resolveRelations";
import { deleteAutoSlug } from "@/utils/autoSlug";
import { parseCityId } from "@/lib/sitemap";
import {
  isExcludedFolder,
  isDataExcludedFolder,
  shouldShowExcludedFolders,
  DATA_EXCLUDED_FOLDERS,
} from "@/constants/excludedFolders";
import {
  getCv,
  setCv,
  setCvNegative,
  getLatestCv,
  setLatestCv,
} from "@repo/utils/storyblok-cv-store";
import { getStoryblokApi } from "./config";
export { getStoryblokApi };
export type {
  FilterOptionData,
  FilterGroupData,
  PrefetchedArticlesData,
} from "@/types/articles";

// Per-pod cap to stay under Storyblok origin rate limit (50 req/s).
const sbLimit = pLimit(5);

let _draftClient: StoryblokClient | null = null;

function getDraftClient(): StoryblokClient {
  if (!_draftClient && process.env.STORYBLOK_DRAFT_TOKEN) {
    _draftClient = new StoryblokClient({
      accessToken: process.env.STORYBLOK_DRAFT_TOKEN,
      region: "us",
    });
  }
  if (!_draftClient) {
    throw new Error("STORYBLOK_DRAFT_TOKEN is not configured");
  }
  return _draftClient;
}

export async function fetchStory(args: {
  version: "draft" | "published";
  domain?: string;
  slug?: string[];
  useDraftToken?: boolean;
}) {
  return sbLimit(() => fetchStoryInternal(args));
}

async function fetchStoryInternal({
  version,
  domain,
  slug,
  useDraftToken = false,
}: {
  version: "draft" | "published";
  domain?: string;
  slug?: string[];
  useDraftToken?: boolean;
}) {
  if (slug?.length && slug[0] === process.env.NEXT_STORYBLOK_ROOT_SLUG) {
    slug = slug.slice(1);
  }

  const correctSlug = [
    process.env.NEXT_PUBLIC_STORYBLOK_ROOT_SLUG || domain,
    slug?.length ? slug.join("/") : null,
  ]
    .filter(Boolean)
    .join("/")
    .replace("//", "/");

  try {
    const api = useDraftToken ? getDraftClient() : getStoryblokApi();

    // Story Version Caching: store cv per-slug in Redis to maximize Storyblok CDN hits.
    // - cv in Redis → pass it → Storyblok CDN serves cached content (fast, cheap)
    // - cv missing (cold start / post-webhook) → flushCache() resets SDK's stale p[token]
    //   → request hits Storyblok origin (cv=0) → fresh content + new cv stored in Redis
    // https://www.storyblok.com/tp/story-version-caching
    let cachedCv: number | null = null;
    let cvParams: { cv?: number } = {};
    if (!useDraftToken) {
      cachedCv = await getCv(correctSlug);
      if (cachedCv !== null) {
        cvParams = { cv: cachedCv };
      } else {
        // cv=0 is falsy so SDK ignores it — must flush SDK's own p[token] instead
        await api.flushCache();
      }
    }

    const data = await api.getStory(
      correctSlug,
      {
        version,
        ...cvParams,
        resolve_links: "url",
        resolve_relations: RESOLVE_RELATIONS,
      },
      {
        ...getNextCachingParams(version),
      },
    );

    // Per-slug cv: skip the write if nothing changed (same key, same value —
    // wasted Redis traffic on a hot cache hit).
    //
    // `__latest__` is the space-wide maximum cv we've ever seen, consumed by
    // the 404 negative-cache. It can advance via publishes of OTHER slugs
    // while this slug's cv stays put — always refresh it on a successful
    // response. cv-store applies an internal monotonic guard, so a stale
    // value can't overwrite a fresher one even under concurrent writes.
    const responseCv = data?.data?.cv;
    if (!useDraftToken && typeof responseCv === "number" && responseCv > 0) {
      const writes: Promise<void>[] = [setLatestCv(responseCv)];
      if (responseCv !== cachedCv) writes.push(setCv(correctSlug, responseCv));
      await Promise.all(writes);
    }

    return data;
  } catch (error) {
    // 404 is definitive (path doesn't exist on Storyblok). Bot scanners pound
    // thousands of unique 404 slugs — without negative-cache each repeat hit
    // pays cv=0 → 301 → 404. Writing `__latest__` cv intco the per-slug key
    // (with TTL, to bound Redis growth) means the second hit on the same
    // slug just gets a fast cv=N → 404. The webhook's `deleteCv(slug)`
    // clears the negative entry early if the path is later published.
    if (
      !useDraftToken &&
      typeof error === "object" &&
      error !== null &&
      "status" in error &&
      (error as { status?: number }).status === 404
    ) {
      const latestCv = await getLatestCv();
      if (latestCv !== null) {
        await setCvNegative(correctSlug, latestCv);
      }
      return null;
    }

    console.error(error);
    return null;
  }
}

export async function fetchRootStory({
  version,
  slug,
  useDraftToken = false,
}: {
  version: "draft" | "published";
  domain?: string;
  slug?: string[];
  useDraftToken?: boolean;
}) {
  const api = useDraftToken ? getDraftClient() : getStoryblokApi();

  const correctSlug = [slug ? slug.join("/") : "/"].filter(Boolean).join("/");

  // Reset storyblok-js-client module-level cv (per-pod RAM state). Without
  // this, after a publish + Next.js fetch cache invalidation, the SDK may
  // still call Storyblok with a stale cv and receive a pre-publish snapshot.
  // Mirrors fetchStory's pattern when cv-store is empty.
  await api.flushCache();

  const data = await api.getStory(
    correctSlug,
    {
      version,
      resolve_links: "story",
      resolve_relations: RESOLVE_RELATIONS,
    },
    {
      ...getNextCachingParams(version),
    },
  );

  return data;
}

export async function fetchStories({
  version,
  domain,
  params,
  useDraftToken = false,
}: {
  version: "draft" | "published";
  domain?: string;
  params: ISbStoriesParams;
  useDraftToken?: boolean;
}) {
  const api = useDraftToken ? getDraftClient() : getStoryblokApi();
  const showExcludedFolders = shouldShowExcludedFolders();

  // Reset storyblok-js-client module-level cv (per-pod RAM state). Critical
  // for global components (header, footer, shareLinks) fetched here from
  // CoreLayout - without this a publish webhook + fetch cache invalidation
  // can still serve stale data on pods whose module cv hasn't advanced.
  await api.flushCache();

  const data = await api.getStories(
    {
      version,
      starts_with: (process.env.NEXT_STORYBLOK_ROOT_SLUG || domain) as string,
      ...params,
    },
    {
      ...getNextCachingParams(version),
    },
  );

  // Filter out stories from data-excluded folders in production
  // Note: global-components and configuration are NOT filtered here
  if (!showExcludedFolders && data.data?.stories) {
    data.data.stories = data.data.stories.filter(
      (story) => !isDataExcludedFolder(story.full_slug || ""),
    );
  }

  return data;
}

export async function fetchStoriesForSitemap({
  version,
}: {
  version: "draft" | "published";
}) {
  const api = getStoryblokApi();
  const pageTypes = ["page", "article", "locationPage"];
  const showExcludedFolders = shouldShowExcludedFolders();

  const promises = pageTypes.map((contentType) =>
    api.getAll(
      "cdn/stories",
      {
        version,
        content_type: contentType,
        starts_with: `${process.env.NEXT_STORYBLOK_ROOT_SLUG as string}`,
        // Drop the content tree from the response — sitemap code only needs
        // full_slug + updated_at (both top-level on the story object). Without
        // this, each story carries 5-50KB of unused content; locationPage has
        // thousands of entries and the cumulative allocation OOM'd V8 heap.
        // Trade-off: filterIndexableStories can no longer check content.robots
        // (`s.content` is undefined); no-index pages enter the sitemap but
        // search engines still respect the page-level <meta name="robots">.
        excluding_fields: "content",
      },
      "stories",
      {
        ...getNextCachingParams("published", 3600),
      },
    ),
  );

  const results = await Promise.all(promises);
  const allStories: ISbStoryData<Page>[] = results.flat();

  // Filter out excluded folders if in production
  if (!showExcludedFolders) {
    return allStories.filter(
      (story) => !isExcludedFolder(story.full_slug || ""),
    );
  }

  return allStories;
}

/**
 * Fetches only locationPage stories (for sitemap index and cities).
 */
export async function fetchLocationStoriesForSitemap({
  version,
}: {
  version: "draft" | "published";
}) {
  const api = getStoryblokApi();
  const showExcludedFolders = shouldShowExcludedFolders();

  const stories: ISbStoryData<PageStoryblok>[] = await api.getAll(
    "cdn/stories",
    {
      version,
      content_type: "locationPage",
      starts_with: `${process.env.NEXT_STORYBLOK_ROOT_SLUG as string}`,
      // Drop content tree to avoid V8 heap OOM (see fetchStoriesForSitemap above).
      excluding_fields: "content",
    },
    "stories",
    {
      ...getNextCachingParams("published", 3600),
    },
  );

  if (!showExcludedFolders) {
    return stories.filter((story) => !isExcludedFolder(story.full_slug || ""));
  }

  return stories;
}

/**
 * Fetches page and article stories (non-location content) for sitemap.
 */
export async function fetchPageStoriesForSitemap({
  version,
}: {
  version: "draft" | "published";
}) {
  const api = getStoryblokApi();
  const showExcludedFolders = shouldShowExcludedFolders();
  const pageTypes = ["page", "article"];

  const promises = pageTypes.map((contentType) =>
    api.getAll(
      "cdn/stories",
      {
        version,
        content_type: contentType,
        starts_with: `${process.env.NEXT_STORYBLOK_ROOT_SLUG as string}`,
        // Drop content tree to avoid V8 heap OOM (see fetchStoriesForSitemap above).
        excluding_fields: "content",
      },
      "stories",
      {
        ...getNextCachingParams("published", 3600),
      },
    ),
  );

  const results = await Promise.all(promises);
  const allStories: ISbStoryData<Page>[] = results.flat();

  if (!showExcludedFolders) {
    return allStories.filter(
      (story) => !isExcludedFolder(story.full_slug || ""),
    );
  }

  return allStories;
}

/**
 * Fetches locationPage stories scoped to a specific city.
 * Accepts a composite cityId (e.g. "ny-new-york-city-parking") and
 * splits it into regionCode/citySlug for the Storyblok path.
 */
export async function fetchLocationStoriesByState({
  version,
  stateCode,
}: {
  version: "draft" | "published";
  stateCode: string;
}) {
  const api = getStoryblokApi();
  const showExcludedFolders = shouldShowExcludedFolders();
  const rootSlug = process.env.NEXT_STORYBLOK_ROOT_SLUG as string;

  const { regionCode, citySlug } = parseCityId(stateCode);

  const stories: ISbStoryData<PageStoryblok>[] = await api.getAll(
    "cdn/stories",
    {
      version,
      content_type: "locationPage",
      starts_with: `${rootSlug}/parking/locations/${regionCode}/${citySlug}/`,
    },
    "stories",
    {
      ...getNextCachingParams("published", 3600),
    },
  );

  if (!showExcludedFolders) {
    return stories.filter((story) => !isExcludedFolder(story.full_slug || ""));
  }

  return stories;
}

export async function generateStoryMetadata({
  version,
  domain,
  slug,
  useDraftToken = false,
}: {
  version: "draft" | "published";
  domain?: string;
  slug?: string[];
  useDraftToken?: boolean;
}) {
  const storyResult = await fetchStory({
    version,
    domain,
    slug,
    useDraftToken,
  });

  if (!storyResult || !storyResult.data?.story) {
    console.log(`missing metadata for story: ${slug?.join("/")}`);
    return {};
  }

  const { story } = storyResult.data;

  const canonical = new URL(
    `${process.env.NEXT_PUBLIC_DOMAIN as string}/${deleteAutoSlug(story.full_slug)}`,
  ).toString();

  const defaultOgImage = `${process.env.NEXT_PUBLIC_DOMAIN}/images/logo-background.png`;
  const ogImageUrl = story.content?.ogImage?.filename
    ? `${story.content.ogImage.filename}/m/1200x630/filters:quality(75)`
    : defaultOgImage;

  const openGraph: Metadata["openGraph"] = {
    type: "website",
    title: story.content.seoTitle || story.name || "",
    description: story.content.seoDescription || "",
    url: canonical,
    siteName: "ParkMobile",
    images: [
      {
        url: ogImageUrl,
        width: 1200,
        height: 630,
      },
    ],
  };

  return {
    alternates: {
      canonical,
    },
    metadataBase: new URL(process.env.NEXT_PUBLIC_DOMAIN as string),
    title: story.content.seoTitle || story.name || "",
    description: story.content.seoDescription || "",
    openGraph,
    keywords: story.content?.seoKeywords || "",
    robots:
      story?.content?.robots === "index" ? { index: true } : { index: false },
  };
}

export async function fetchAllLinks({
  version,
}: {
  version: "draft" | "published";
}) {
  try {
    const api = getStoryblokApi();

    const links: ISbLink[] = await api.getAll(
      "cdn/links",
      {
        version,
        per_page: 1000,
        starts_with: `${process.env.NEXT_STORYBLOK_ROOT_SLUG as string}`,
      },
      "links",
      {
        ...getNextCachingParams(version),
      },
    );

    const showExcludedFolders = shouldShowExcludedFolders();

    return links.filter((link) => {
      // Always filter out folders (only include actual pages)
      if (link.is_folder === true) {
        return false;
      }

      // Filter out technical folders in production
      if (!showExcludedFolders && link.slug && isExcludedFolder(link.slug)) {
        return false;
      }

      return true;
    });
  } catch (error) {
    console.error(error);
    return [];
  }
}

export async function fetchArticles({
  version,
  domain,
  perPage,
  page,
  filterQuery,
  sortBy,
  excludeUuids,
  searchTerm,
}: {
  version: "draft" | "published";
  domain?: string;
  perPage: number;
  page: number;
  filterQuery?: Record<string, Record<string, string | null>>;
  sortBy?: string;
  excludeUuids?: string[];
  searchTerm?: string;
}) {
  const api = getStoryblokApi();
  const showExcludedFolders = shouldShowExcludedFolders();

  const baseSlug = `${(process.env.NEXT_STORYBLOK_ROOT_SLUG || domain) as string}`;

  const finalFilterQuery =
    excludeUuids && excludeUuids.length > 0
      ? {
          ...filterQuery,
          uuid: {
            not_in: excludeUuids.join(","),
          },
        }
      : filterQuery;

  // Build excluding_slugs for API-level filtering of data-excluded folders
  const excludingSlugs = !showExcludedFolders
    ? DATA_EXCLUDED_FOLDERS.map((folder) => `${baseSlug}/${folder}/*`).join(",")
    : undefined;

  const response = await api.getStories(
    {
      version: "published",
      starts_with: baseSlug,
      content_type: "article",
      per_page: perPage,
      page,
      filter_query: finalFilterQuery,
      sort_by: sortBy,
      resolve_relations: RESOLVE_RELATIONS,
      ...(searchTerm && { search_term: searchTerm }),
      ...(excludingSlugs && { excluding_slugs: excludingSlugs }),
    },
    {
      ...getNextCachingParams(version),
    },
  );

  // No need for client-side filtering anymore - API handles it
  return response;
}

export async function fetchFolders({
  version,
  params,
}: {
  version: "draft" | "published";
  params: ISbStoriesParams;
}) {
  const api = getStoryblokApi();
  const showExcludedFolders = shouldShowExcludedFolders();

  const allLinks: ISbLink[] = await api.getAll(
    "cdn/links",
    {
      version,
      ...params,
    },
    "links",
  );

  return allLinks.filter((link) => {
    // Only include folders
    if (!link.is_folder) return false;

    // Filter out data-excluded folders in production
    // Note: global-components and configuration are NOT filtered here
    if (!showExcludedFolders && link.slug && isDataExcludedFolder(link.slug)) {
      return false;
    }

    return true;
  });
}

export async function fetchStoriesByUuids({
  version,
  uuids,
}: {
  version: "draft" | "published";
  uuids: string[];
}) {
  if (!uuids || uuids.length === 0) {
    return [];
  }

  const api = getStoryblokApi();

  const { data } = await api.getStories(
    {
      version,
      by_uuids: uuids.join(","),
      resolve_relations: RESOLVE_RELATIONS,
    },
    {
      ...getNextCachingParams(version),
    },
  );

  return data.stories;
}

export async function fetchRedirectsConfig({
  version = "draft",
}: {
  version?: "draft" | "published";
} = {}) {
  try {
    // Draft version requires the draft/preview token — the published CDN
    // token gets a 401 Unauthorized when asked for draft content.
    const api = version === "draft" ? getDraftClient() : getStoryblokApi();
    // Reset storyblok-js-client module-level `cv` cache that lives in Node.js
    // process memory per pod. Without this, after a publish + Next.js fetch
    // cache invalidation, the SDK can still call Storyblok with a stale cv
    // and receive a snapshot from BEFORE the publish - producing broken
    // redirect data on the pod that has the stale cv. Mirrors the pattern
    // fetchStory uses when its cv-store is empty.
    await api.flushCache();
    const response = await api.getStory(
      `${process.env.NEXT_PUBLIC_STORYBLOK_ROOT_SLUG || process.env.NEXT_STORYBLOK_ROOT_SLUG}/configuration/redirects-configuration`,
      {
        resolve_links: "url",
        version,
      },
      {
        ...getNextCachingParams(version, 3600),
      },
    );
    return response?.data?.story;
  } catch (error) {
    console.error("Error fetching redirects config:", error);
    return null;
  }
}

export interface BreadcrumbItem {
  title: string;
  url?: string;
}

function getSlugsFromFullSlug(fullSlug: string): string[] {
  const segments = fullSlug.split("/").filter(Boolean);
  return segments.reduce((paths: string[], _segment, index) => {
    const path = segments.slice(0, index + 1).join("/");
    paths.push(path);
    return paths;
  }, []);
}

function sortStoriesBySlugLength(stories: ISbStoryData[]): ISbStoryData[] {
  return stories.sort(
    (a, b) => (a.full_slug?.length || 0) - (b.full_slug?.length || 0),
  );
}

import { PrefetchedArticlesData, FilterGroupData } from "@/types/articles";

export async function prefetchArticlesGridData({
  version,
  domain,
  perPage = 8,
  page = 1,
  specificArticleType,
  excludeUuids = [],
}: {
  version: "draft" | "published";
  domain?: string;
  perPage?: number;
  page?: number;
  specificArticleType?: string;
  excludeUuids?: string[];
}): Promise<PrefetchedArticlesData> {
  const finalDomain = (process.env.NEXT_STORYBLOK_ROOT_SLUG ||
    domain) as string;
  const rootFilterPath = `${finalDomain}/global-components/filters/`;

  const [filterFoldersResponse, folders, articlesResponse] = await Promise.all([
    fetchStories({
      version,
      params: {
        starts_with: rootFilterPath,
        sort_by: "position:asc",
      },
    }),
    fetchFolders({
      version,
      params: { starts_with: rootFilterPath },
    }),
    fetchArticles({
      version,
      domain: finalDomain,
      perPage,
      page,
      filterQuery: specificArticleType
        ? { type: { in: specificArticleType } }
        : undefined,
      sortBy: "content.publicationDate:desc",
      excludeUuids,
    }),
  ]);

  const loadedFilterGroups: FilterGroupData[] = [];

  for (const folder of folders) {
    const folderName = folder.name!;
    const folderKey = folder.slug?.split("/").pop() || folderName;

    const relatedOptions = filterFoldersResponse.data.stories.filter(
      (story) => story.parent_id === folder.id,
    );

    const mappedOptions = relatedOptions
      .filter((option) => option.content.component !== "filterConfig")
      .map((option) => ({
        uuid: option.uuid,
        title: option.content.title as string,
        value: option.content.value as string | undefined,
      }))
      .sort((a, b) => {
        const aStory = relatedOptions.find((story) => story.uuid === a.uuid);
        const bStory = relatedOptions.find((story) => story.uuid === b.uuid);
        return (aStory?.position || 0) - (bStory?.position || 0);
      });

    if (mappedOptions.length > 0) {
      loadedFilterGroups.push({
        key: folderKey,
        label: folderName,
        options: mappedOptions,
        position: folder.position || 0,
      });
    }
  }

  const orderedFilterGroups = loadedFilterGroups.sort(
    (a, b) => a.position - b.position,
  );

  return {
    filters: orderedFilterGroups,
    articles: articlesResponse.data.stories as ISbStoryData[],
    total: articlesResponse.total,
    perPage,
    page,
  };
}

const CATEGORY_TITLES: Record<string, string> = {
  "parking-lot": "Parking Lots",
  destination: "Destinations",
  neighbourhood: "Neighborhoods",
  "point-of-interest": "Points of Interest",
};

function getCategoryFromSlug(slug: string): string | null {
  const segments = slug.split("/").filter(Boolean);
  const locationsIndex = segments.findIndex((seg) => seg === "locations");

  if (locationsIndex === -1) return null;

  const locationSegments = segments.slice(locationsIndex + 1);
  const category = locationSegments[2];

  return category || null;
}

export async function fetchBreadcrumbs({
  version,
  story,
  useDraftToken = false,
}: {
  version: "draft" | "published";
  story: Pick<ISbStoryData, "full_slug" | "name">;
  useDraftToken?: boolean;
}): Promise<BreadcrumbItem[]> {
  try {
    if (!story.full_slug) {
      return [];
    }

    const segments = story.full_slug.split("/").filter(Boolean);
    const locationsIndex = segments.findIndex((seg) => seg === "locations");

    if (locationsIndex === -1) {
      return [];
    }

    const locationSegments = segments.slice(locationsIndex + 1);
    const stateCode = locationSegments[0];
    const rootSlug = process.env.NEXT_STORYBLOK_ROOT_SLUG || "";

    const allSlugs = getSlugsFromFullSlug(story.full_slug);
    const stateSlug = `${rootSlug}/parking/locations/${stateCode}`;

    const slugsToFetch = allSlugs
      .filter((slug) => {
        return slug.length > stateSlug.length;
      })
      .slice(0, -1);

    let parentStories: ISbStoryData[] = [];

    if (slugsToFetch.length > 0) {
      const parentSlugsWithTrailingSlash = slugsToFetch.map(
        (slug) => `${slug}/`,
      );

      const {
        data: { stories },
      } = await fetchStories({
        version,
        useDraftToken,
        params: {
          by_slugs: parentSlugsWithTrailingSlash.join(","),
          resolve_links: "url",
        },
      });

      parentStories = sortStoriesBySlugLength(stories as ISbStoryData[]);
    }

    const breadcrumbs: BreadcrumbItem[] = [];

    breadcrumbs.push({
      title: "Home",
      url: "/parking/locations",
    });

    if (stateCode) {
      breadcrumbs.push({
        title: stateCode.toUpperCase(),
      });
    }

    const parentBreadcrumbs = parentStories.map((parentStory) => {
      const slug = parentStory.full_slug || "";
      const category = getCategoryFromSlug(slug);

      const title =
        category && CATEGORY_TITLES[category]
          ? CATEGORY_TITLES[category]
          : parentStory.name || "";

      return {
        title,
        url: `/${deleteAutoSlug(slug)}`,
      };
    });

    breadcrumbs.push(...parentBreadcrumbs);

    if (locationsIndex !== -1) {
      const locationSegments = segments.slice(locationsIndex + 1);
      const category = locationSegments[2];

      if (category !== "parking-lot") {
        breadcrumbs.push({
          title: story.name || "",
          url: `/${deleteAutoSlug(story.full_slug)}`,
        });
      }
    } else {
      breadcrumbs.push({
        title: story.name || "",
        url: `/${deleteAutoSlug(story.full_slug)}`,
      });
    }

    return breadcrumbs;
  } catch (error) {
    console.error("Error fetching breadcrumbs:", error);
    if (story.full_slug && story.name) {
      return [
        {
          title: story.name,
          url: `/${deleteAutoSlug(story.full_slug)}`,
        },
      ];
    }
    return [];
  }
}
